import { forwardRef, MutableRefObject, Ref, useCallback, useEffect, useRef, useState } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Chip } from '@mui/material';

import FilterPopper from '../FilterPopper/FilterPopper';
import TextQueryForm from '../FilterPopper/TextQueryForm';
import Filter, { NumberFilter, TextFilter } from '../models/Filter';

interface TextFilterChipProps {
  filter: TextFilter | NumberFilter;
  onClick: (filter: Filter) => void;
  onDelete: (filter: Filter) => void;
}

interface TextFilterPopoverProps {
  popoverOpen: boolean;
  filter: TextFilter | NumberFilter;
  onClosePopover: (oldFilter: Filter, newFilter?: Filter) => void;
}

interface TextFilterChipWithPopoverProps {
  filter: TextFilter | NumberFilter;
  onClick?: (filter: Filter) => void;
  onDelete?: (filter: Filter) => void;
  onClosePopover?: (oldFilter: Filter, newFilter?: Filter) => void;
  'data-testid'?: string;
}

function formatPropertyValue(value: string): string {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return value;
  }
  return number.toLocaleString();
}

const TextFilterChip = forwardRef(
  ({ filter, onClick, onDelete }: TextFilterChipProps, ref: Ref<HTMLDivElement>) => {
    const handleClick = useCallback(() => onClick?.(filter), [onClick, filter]);
    const handleDelete = useCallback(() => onDelete?.(filter), [onDelete, filter]);
    const color = filter.hasError ? 'error' : undefined;
    const icon = filter.hasError ? <ErrorOutlineIcon data-testid="error-icon" /> : undefined;
    const label =
      filter.relation.label && filter.value
        ? `${filter.property.label} ${filter.relation.label} ${formatPropertyValue(filter.value)}`
        : filter.property.label;

    return (
      <Chip
        ref={ref}
        label={label}
        color={color}
        icon={icon}
        onClick={handleClick}
        onDelete={handleDelete}
        sx={{ maxWidth: 340 }}
        data-testid={filter.property.label}
      />
    );
  }
);
TextFilterChip.displayName = 'TextFilterChip';

const TextFilterPopover = forwardRef(
  (
    { popoverOpen, filter, onClosePopover }: TextFilterPopoverProps,
    chipRef: MutableRefObject<HTMLDivElement>
  ) => {
    const [relation, setRelation] = useState(filter.relation);
    const [value, setValue] = useState(filter.value);

    const handleClosePopover = useCallback(
      (applyChanges: boolean) => {
        if (applyChanges) {
          onClosePopover?.(filter, { ...filter, relation, value });
        } else {
          onClosePopover?.(filter, { ...filter, hasError: true });
        }
      },
      [onClosePopover, filter, relation, value]
    );

    return (
      chipRef.current && (
        <FilterPopper
          anchorEl={chipRef.current}
          open={popoverOpen}
          onClose={handleClosePopover}
          title={filter.property.label}
        >
          <TextQueryForm
            relations={filter.property.relations}
            relationSelected={relation}
            value={value}
            onChangeRelation={setRelation}
            onChangeValue={setValue}
            type={filter.property.type}
          />
        </FilterPopper>
      )
    );
  }
);
TextFilterPopover.displayName = 'TextFilterPopover';

const TextFilterChipWithPopover = ({
  filter,
  onClick,
  onDelete,
  onClosePopover,
  'data-testid': dataTestId,
}: TextFilterChipWithPopoverProps) => {
  const chipRef = useRef(null);

  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    if (chipRef.current) {
      setPopoverOpen(filter.open);
    }
  }, [chipRef, setPopoverOpen, filter]);

  return (
    <Box data-testid={dataTestId}>
      <TextFilterChip ref={chipRef} filter={filter} onClick={onClick} onDelete={onDelete} />
      <TextFilterPopover
        ref={chipRef}
        popoverOpen={popoverOpen}
        filter={filter}
        onClosePopover={onClosePopover}
      />
    </Box>
  );
};
TextFilterChipWithPopover.displayName = 'TextFilterChipWithPopover';

export { TextFilterChipWithPopoverProps as TextFilterChipProps };
export default TextFilterChipWithPopover;
